/**
 * Машинный код отказа сервера — чтобы различать причины не по тексту.
 *
 * ⚠️ ФОРМА ОШИБКИ — ТА, ЧТО ОТДАЁТ КЛИЕНТ API. Перехватчик в `api/client.ts`
 * (`normaliseError`) превращает ошибку axios в `{status, message, raw}`, где
 * `raw` — тело ответа сервера. Поля `response` у неё нет. До 10.09.2026 проверка
 * `grading_mode_auto` искала код в `response.data` и в живом приложении не
 * срабатывала ни разу. Сырая форма axios читается тоже — на случай вызова мимо
 * клиента.
 *
 * Код сервер кладёт в корень тела рядом с `status` и `message`; текст `message`
 * — для человека и может меняться, различать причины только по `code`.
 */

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

function errorBody(err: unknown): Record<string, unknown> | undefined {
  if (!isObject(err)) return undefined;
  if (isObject(err.raw)) return err.raw;
  const response = err.response;
  if (isObject(response) && isObject(response.data)) return response.data;
  return undefined;
}

export function apiErrorCode(err: unknown): string | undefined {
  const code = errorBody(err)?.code;
  return typeof code === 'string' && code !== '' ? code : undefined;
}

/** Кто держит адрес из отказа `409 email_taken` смены почты (договор `admin-email-change.md`). */
export function apiErrorHolder(err: unknown): { full_name: string; status: string } | null {
  const h = errorBody(err)?.holder;
  if (!isObject(h)) return null;
  return {
    full_name: typeof h.full_name === 'string' ? h.full_name : '',
    status: typeof h.status === 'string' ? h.status : '',
  };
}

/** Незаполненные задания из отказа `409 open_answers_missing` (договор `empty-open-answers.md`). */
export function apiErrorBlockIds(err: unknown): string[] | null {
  const ids = errorBody(err)?.block_ids;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string' && id !== '') : null;
}

/**
 * Отказы «открытый ответ пустой». Такой ответ не пройдёт никогда, пока ученик
 * его не переделает, поэтому очередь убирает запись, а не держит её неделю.
 *
 * ⚠️ ЧЕТЫРЕ КОДА ЗАПИСИ ДОБАВЛЕНЫ 12.09.2026 (договор `record-integrity.md`).
 * Без них новый отказ сервера — `422 recording_truncated`, `422 empty_recording`,
 * `400 file_empty`, `400 file_unreadable` — молча оседал бы в очереди на неделю
 * (`isPermanent` считает 4xx постоянным, но снимает запись только по коду из
 * этого набора), а ученик был бы уверен, что запись ушла.
 *
 * ⚠️ `audio_too_short` НЕ УБИРАТЬ. Им сервер отвечает ответу, пришедшему без
 * `client_seconds` и `stop_reason`, — в том числе из очереди, залежавшейся с
 * прошлой версии приложения.
 */
export const OPEN_ANSWER_REJECT_CODES: ReadonlySet<string> = new Set([
  'essay_empty',
  'audio_too_short',
  'open_answers_missing',
  'recording_truncated',
  'empty_recording',
  'file_empty',
  'file_unreadable',
]);

/**
 * Отказы «запись голоса опоздала» — `409` ответа на задание (договор `audio-upload.md` §3.1, 02.10.2026).
 *
 * `answer_superseded` — ученик уже сдал это задание позже (с другого устройства); `already_reviewed` — работу
 * уже приняли. Оба — «не пройдёт никогда»: очередь снимает запись, ученику говорится прямо. Приходят только
 * клиенту, который шлёт `client_id`.
 */
export const LATE_RECORDING_CODES: ReadonlySet<string> = new Set(['answer_superseded', 'already_reviewed']);

/**
 * Свой, не серверный код: файла записи на телефоне больше нет (`lib/lessonAudio.ts`). Слать нечего — очередь
 * снимает запись и говорит ученику, а не повторяет отправку пустоты.
 */
export const RECORD_FILE_LOST = 'record_file_lost';

/**
 * Отказы «объекта больше нет»: раздел удалён методистом (`404 section_not_found`, progress-service, техдолг 265).
 * Повтор получит тот же отказ, а запись осела бы в «не отправлено» навсегда — очередь её снимает.
 */
export const GONE_CODES: ReadonlySet<string> = new Set(['section_not_found']);

/** Отказы, после которых запись голоса снимается с очереди сразу. */
export function recordDropCode(code: string | undefined): boolean {
  return !!code && (LATE_RECORDING_CODES.has(code) || code === RECORD_FILE_LOST);
}

/**
 * Номера из `data` отказа конструктора: `questions` у `400 quiz_no_correct_answer`,
 * `statements` у него же для «Верно / Неверно / Не указано»
 * (договор `constructor-v2.md` §1.5).
 *
 * ⚠️ ЭТИ НОМЕРА ЛЕЖАТ ВНУТРИ `data`, а не в корне тела, как `block_ids` выше. Форма
 * отказа у ручек разная, и одной общей читалкой тут не обойтись.
 *
 * Номера приходят С НУЛЯ — человеку их показывать с единицы (`issueNumbers`).
 */
export function apiErrorDataIndexes(err: unknown, field: 'questions' | 'statements'): number[] | null {
  const data = errorBody(err)?.data;
  if (!isObject(data)) return null;
  const raw = data[field];
  if (!Array.isArray(raw)) return null;
  return raw.filter((n): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0);
}

/**
 * Замер записи, который сервер прислал вместе с отказом (договор §2.3).
 *
 * `server_seconds` — длина по звуку, та самая, которой не хватало ученице:
 * она видела на экране 2:30 (свои часы), а в файле было 20 секунд.
 */
export type RecordingReport = {
  server_seconds: number;
  voiced_seconds?: number;
  header_seconds?: number;
  client_seconds?: number;
  stop_reason?: string;
};

/**
 * Уточнение причины отказа: `no_file`, `unreadable`, `silent`, `too_short`,
 * `truncated`. Один код `empty_recording` покрывает четыре разных беды, и
 * ученику о них надо говорить по-разному.
 */
export function apiErrorReason(err: unknown): string | undefined {
  const reason = errorBody(err)?.reason;
  return typeof reason === 'string' && reason !== '' ? reason : undefined;
}

/**
 * Замер из тела отказа.
 *
 * ⚠️ У ОТКАЗА ОБЪЕКТ ЛЕЖИТ В КОРНЕ ТЕЛА, а при успехе — в `data.recording`
 * (договор §2.3). Здесь читается только корень: это разбор ошибки.
 *
 * Без `server_seconds` числа нет вовсе — тогда лучше промолчать, чем показать
 * ученику «сохранилось 0 секунд из 0».
 */
export function apiErrorRecording(err: unknown): RecordingReport | undefined {
  const raw = errorBody(err)?.recording;
  if (!isObject(raw) || typeof raw.server_seconds !== 'number' || !Number.isFinite(raw.server_seconds)) {
    return undefined;
  }
  return raw as RecordingReport;
}
